import React, { useRef, useState, useEffect } from 'react'

// Styles
import {
  Wrapper,
  HiddenInput,
  Digits,
  Digit,
  Error,
} from './VerificationCode.styles'

// Types
export interface VerificationCodeProps {
  /**  The number of digits to render. default: 6 */
  size?: number
  /**  Will focus on the input when it mounts. default: true*/
  autoFocus?: boolean
  /**  Renders the digits in a disabled state */
  isDisabled?: boolean
  /**  An error message */
  error?: string
  /**  Event fired when user fills the last digit */
  onFilled: (value: string) => void
}

/**
 * VerificationCode
 *
 * Renders the 6-digit 2FA verification code inputs
 * By default it renders 6 digit inputs
 */
const VerificationCode: React.FC<VerificationCodeProps> = ({
  autoFocus = true,
  size = 6,
  isDisabled,
  error,
  onFilled,
}) => {
  const hiddenInput = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')
  const [isFocused, setIsFocused] = useState(autoFocus)
  const [localError, setLocalError] = useState<string | undefined>(error)

  /* Autofocus hidden input to allow the user to start typing right away */
  useEffect(() => {
    if (hiddenInput.current && autoFocus) {
      hiddenInput.current.focus()
    }
  }, [autoFocus])

  /* Set local error with "error" prop if it changes */
  useEffect(() => {
    if (error) {
      setLocalError(error)
    }
  }, [error])

  /* Trigger onFilled when user fills the last digit */
  useEffect(() => {
    if (value.length === size) {
      onFilled?.(value)
    }
  }, [size, value, onFilled])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Filter string and get only digits, slice for the first 6 characters
    const onlyDigits = event.target.value.replace(/\D/g, '').slice(0, size)
    setValue(onlyDigits)

    // Clear error
    if (value.length < size) {
      setLocalError(undefined)
    }
  }

  /* Focus on the hidden input when the user clicks the digits*/
  const handleClick = () => {
    hiddenInput?.current?.focus()
  }

  return (
    <Wrapper>
      <HiddenInput
        data-testid="verification-code"
        className="sr-only"
        type="text"
        aria-label="Verification Code"
        ref={hiddenInput}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={isDisabled}
        value={value}
      />
      <Digits onClick={handleClick}>
        {[...Array(size)].map((_, index) => {
          // Keep the next empty digit in active state
          // If its last digit, keep it active even if filled
          const isActive =
            index + 1 === size && value.length === size
              ? true
              : value.length === index

          return (
            <Digit
              data-testid="verification-code-digit"
              key={index}
              onClick={handleClick}
              $isActive={isFocused && isActive}
              $isDisabled={isDisabled}
              $hasError={!!localError}>
              {value[index]}
            </Digit>
          )
        })}
      </Digits>
      {!!localError && <Error>{localError}</Error>}
    </Wrapper>
  )
}
export default VerificationCode
